// src/features/readingnote/components/ReadingCalendar.tsx
import React, { useMemo, useState } from "react";
import { withBase } from "@/ui/Header";
import { toCoverUrl } from "@/lib/coverUrl";
import { COLOR, FW, RADIUS } from "@/ui/tokens";

const ICONS = {
    prev: withBase("icons/prev1.svg"),
    next: withBase("icons/next1.svg"),
};

type Cover = { book_id: number | string; cover_url?: string; status?: "READING"|"WISHLIST"|"FINISHED" };
export type CalendarEntry = { date: string; covers: Cover[] };

function pad2(n: number) { return String(n).padStart(2, "0"); }
function ymd(d: Date) { return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`; }
function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth()+1, 0); }

// 월 매트릭스(6주 × 7일) 생성
// 월 매트릭스(4~6주 × 7일) 생성
function buildMatrix(base: Date, weekStartsOn: 0|1 = 0): Date[][] {
    const first = startOfMonth(base);
    const last  = endOfMonth(base);
    const firstW = (first.getDay() - weekStartsOn + 7) % 7; // 앞쪽 빈칸
    const totalDays = last.getDate();
    const weeks = Math.ceil((firstW + totalDays) / 7); // 4~6
    const showDayNumbers = true;
    const cells: Date[] = [];

    // 앞쪽 지난달
    for (let i = 0; i < firstW; i++) {
        const d = new Date(first);
        d.setDate(d.getDate() - (firstW - i));
        cells.push(d);
    }

    // 이번달
    for (let i = 1; i <= totalDays; i++) {
        cells.push(new Date(base.getFullYear(), base.getMonth(), i));
    }

    // 뒤쪽 다음달 (weeks*7만큼만 채움)
    const tail = weeks * 7 - cells.length;
    for (let i = 1; i <= tail; i++) {
        const d = new Date(last);
        d.setDate(last.getDate() + i);
        cells.push(d);
    }

    // weeks x 7
    const rows: Date[][] = [];
    for (let r = 0; r < weeks; r++) rows.push(cells.slice(r * 7, r * 7 + 7));
    return rows;
}

export function ReadingCalendar({
                                    entries = [],
                                    px,
                                    s,
                                    sfpx,
                                    isSpc = false,
                                    onDayClick,
                                    onMonthChange,
                                    weekStartsOn = 0, // 0: 일요일, 1: 월요일
                                }: {
    entries: CalendarEntry[];
    px: (n: number) => string;
    s: (n: number) => number;
    sfpx: (n: number) => string;
    isSpc?: boolean;
    onDayClick?: (isoDate: string, covers: Cover[]) => void;
    onMonthChange?: (month: Date) => void;
    weekStartsOn?: 0|1;
}) {
    const [month, setMonth] = useState<Date>(() => startOfMonth(new Date()));
    React.useEffect(() => { onMonthChange?.(month); }, [month, onMonthChange]);
    const today = ymd(new Date());

    // date → covers[] 매핑
    const map = useMemo(() => {
        const m = new Map<string, Cover[]>();
        for (const e of entries) m.set(e.date, e.covers || []);
        return m;
    }, [entries]);

    const rows = useMemo(() => buildMatrix(month, weekStartsOn), [month, weekStartsOn]);

    // 레이아웃 상수
    const ARROW_BTN = isSpc ? 96 : 64;
    const ARROW_ICON = isSpc ? 72 : 48;
    const GAP = s(12);
    const HEADER_FZ = s(48);
    const DATE_FZ = s(18);
    const DATE_H = s(48);

    const weeks = rows.length; // 4~6

    // SPC: tall scrollable cells. Others: fixed size (slightly smaller for 6-week months).
    const CELL_H = isSpc ? s(520) : weeks >= 6 ? s(300) : s(370);

    const gridH = CELL_H * weeks + GAP * (weeks - 1);

    return (
        <div style={{width: "100%"}}>
            {/* 헤더 (월 네비게이션) — 월 텍스트 기준으로 < 2025.01 > 중앙 정렬 */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "auto auto auto", // 좌버튼 / 월 / 우버튼
                    alignItems: "center",
                    justifyContent: "center",             // 전체를 가운데로 모음
                    columnGap: px(200),                    // 화살표-텍스트 간격 (원하면 16~32로 조절)
                    width: "100%",
                    marginBottom: px(16),
                }}
            >
                <button
                    onClick={() => setMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
                    aria-label="이전 달"
                    style={{
                        width: px(ARROW_BTN),
                        height: px(ARROW_BTN),
                        borderRadius: 12,
                        display: "grid",
                        placeItems: "center",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                    }}
                >
                    <img
                        src={ICONS.prev}
                        alt=""
                        style={{
                            width: px(ARROW_ICON),
                            height: px(ARROW_ICON),
                            display: "block",
                            filter: "brightness(0)",
                        }}
                    />
                </button>

                <div
                    style={{
                        fontSize: sfpx(48),
                        fontWeight: FW.black,
                        textAlign: "center",
                        lineHeight: px(64), // 버튼 높이와 맞춰 시각적 중앙
                        height: px(64),
                        letterSpacing: "-0.8px",
                        padding: `0 ${px(8)}`,
                    }}
                >
                    {month.getFullYear()}.{pad2(month.getMonth() + 1)}
                </div>

                <button
                    onClick={() => setMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
                    aria-label="다음 달"
                    style={{
                        width: px(ARROW_BTN),
                        height: px(ARROW_BTN),
                        borderRadius: 12,
                        display: "grid",
                        placeItems: "center",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                    }}
                >
                    <img
                        src={ICONS.next}
                        alt=""
                        style={{
                            width: px(ARROW_ICON),
                            height: px(ARROW_ICON),
                            display: "block",
                            filter: "brightness(0)",
                        }}
                    />
                </button>
            </div>

            {/* 요일 라벨 */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    columnGap: px(GAP),
                    marginBottom: px(8),
                    color: "#666",
                    fontWeight: FW.bold
                }}
            >
                {(weekStartsOn === 1 ? ["월", "화", "수", "목", "금", "토", "일"] : ["일", "월", "화", "수", "목", "금", "토"]).map((d) => (
                    <div key={d} style={{textAlign: "center"}}>{d}</div>
                ))}
            </div>

            {/* 달력 그리드 */}
            <div style={{minHeight: px(gridH), overflowY: weeks > 5 ? "auto" : "visible", ...(weeks > 5 ? {height: px(gridH)} : {})}}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, 1fr)",
                        gridAutoRows: px(CELL_H),
                        columnGap: px(GAP),
                        rowGap: px(GAP),
                    }}
                >
                    {rows.flat().map((d, idx) => {
                        const iso = ymd(d);
                        const inMonth =
                            d.getFullYear() === month.getFullYear() &&
                            d.getMonth() === month.getMonth();

                        const covers = map.get(iso) || [];
                        const first = covers[0];

                        const baseBox = {
                            width: "100%",
                            height: "100%",
                            padding: s(6),
                            position: "relative" as const,
                            border: "none",
                            background: COLOR.white,
                            display: "flex" as const,
                            flexDirection: "column" as const,
                            alignItems: "center" as const,
                            justifyContent: "flex-start" as const,
                        };

                        const isClickable = inMonth;

                        return (
                            <button
                                key={iso + "-" + idx}
                                disabled={!isClickable}
                                onClick={() => {
                                    if (!isClickable) return;
                                    onDayClick?.(iso, covers);
                                }}
                                style={{
                                    ...baseBox,
                                    cursor: isClickable ? "pointer" : "default",
                                    opacity: inMonth ? 1 : 0.35,
                                    pointerEvents: isClickable ? "auto" : "none", // 완전 차단(선택)
                                }}
                            >
                                {/* 날짜 (위쪽 정렬) */}
                                <div
                                    style={{
                                        fontSize: sfpx(18),
                                        fontWeight: FW.extraBold,
                                        color: COLOR.black,
                                        lineHeight: px(DATE_H),
                                        textAlign: "center",
                                    }}
                                >
                                    {inMonth ? d.getDate() : ""}
                                </div>
                                {/* 표지 — 셀 전체를 덮어 날짜까지 가림 */}
                                {inMonth && first?.cover_url && (
                                    <div style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        zIndex: 1,
                                        borderRadius: s(10),
                                        overflow: "hidden",
                                    }}>
                                        <img
                                            src={toCoverUrl(first.cover_url)}
                                            alt=""
                                            loading="lazy"
                                            referrerPolicy="no-referrer"
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                display: "block",
                                            }}
                                        />
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
